import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/#about' },
    { name: 'Services', href: '/#services' },
    { name: 'Menu', href: '/#menu' },
    { name: 'Matchday Catering', href: '/matchday-catering' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/#contact' }
  ];

  const services = [
    'Corporate Events',
    'Parties & Celebrations',
    'Funerals & Wakes',
    'Festivals',
    'Matchday Catering',
    'Special Occasions'
  ];

  return (
    <footer className="bg-white border-t" style={{ borderColor: 'var(--color-accent-coconut)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-ui-accent)' }}>
              Marlowes Catering
            </h3>
            <p className="mb-4" style={{ color: 'var(--text-subtle-light)' }}>
              Professional catering service in Chadderton, Oldham. Serving Greater Manchester with fresh, homemade food and a 5-star food hygiene rating.
            </p>
            <div className="mb-4">
              <a
                href="https://www.google.com/search?client=safari&hs=DyOU&sca_esv=672cfb55ba862964&hl=en-gb&sxsrf=AE3TifOjAcwc3XvPkZr_L4umMVdH28Qabg:1767041600723&q=marlowes+catering+oldham+reviews&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E5KEcp5FkaiUq3VTEW0Lx_8UiF-EJKKq4SbPNWWdMqnA0YbO33-e3hXy1MHUo3ETkGtmDKA-4Qxy3z2_JEpsL0lA2RIl7FNsWTEXyF5TsEvvSJ3Dbg%3D%3D&sa=X&ved=2ahUKEwixjKj31uORAxWXWkEAHYRgIwEQ9qsLegQIFBAJ&biw=390&bih=663&dpr=3#ebo=3"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--accent-ui-accent)', color: 'white' }}
              >
                ⭐ Review Us on Google
              </a>
            </div>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/share/17sk47SFGE/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-opacity hover:opacity-70"
                aria-label="Visit our Facebook page"
              >
                <Facebook size={24} style={{ color: 'var(--accent-ui-accent)' }} />
              </a>
              <a
                href="https://twitter.com/marlowes_chad"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-opacity hover:opacity-70"
                aria-label="Visit our Twitter profile"
              >
                <Twitter size={24} style={{ color: 'var(--accent-ui-accent)' }} />
              </a>
              <a
                href="https://www.instagram.com/marlowes_chad/"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-opacity hover:opacity-70"
                aria-label="Visit our Instagram profile"
              >
                <Instagram size={24} style={{ color: 'var(--accent-ui-accent)' }} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4" style={{ color: 'var(--text-base)' }}>
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="transition-opacity hover:opacity-70"
                    style={{ color: 'var(--text-subtle-light)' }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-4" style={{ color: 'var(--text-base)' }}>
              Our Services
            </h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service} style={{ color: 'var(--text-subtle-light)' }}>
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-4" style={{ color: 'var(--text-base)' }}>
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:01612228831"
                  className="flex items-center space-x-2 transition-opacity hover:opacity-70"
                  style={{ color: 'var(--text-subtle-light)' }}
                >
                  <Phone size={18} />
                  <span>0161 222 8831</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@marlowescatering.co.uk"
                  className="flex items-center space-x-2 transition-opacity hover:opacity-70"
                  style={{ color: 'var(--text-subtle-light)' }}
                >
                  <Mail size={18} />
                  <span>info@marlowescatering.co.uk</span>
                </a>
              </li>
              <li className="flex items-start space-x-2" style={{ color: 'var(--text-subtle-light)' }}>
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <span>Chadderton, Oldham OL9<br />Greater Manchester</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="pt-8 border-t text-center"
          style={{ borderColor: 'var(--color-accent-coconut)' }}
        >
          <p style={{ color: 'var(--text-subtle-light)' }}>
            &copy; {currentYear} Marlowes Catering Limited. Company No. 16218152. All rights reserved.
          </p>
          <p className="mt-2" style={{ color: 'var(--text-subtle-light)' }}>
            Registered in England and Wales | Catering for all occasions across Greater Manchester
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;